import * as React from 'react'
import { useGetAllArticles, useGetAllCategories, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { useGetCapacitiesQuery } from 'capacity/reduxQuery'
import { Reveal } from 'uiComponents/reveal/reveal'
import { useTranslation } from 'translations'
import { CapacitySectionShell } from './components/sectionShell'
import { SectionPart } from './components/sectionPart'
import { categoryCapacityPart, categoryTimeSlotsPart } from './model/sectionState'
import { categoryCapacityRows } from './model/capacityRows'
import { categoryGroupBlocks, productsLosingTimeSlots } from './model/groupBlocks'
import { useGroupBlocks } from './useGroupBlocks'

interface CategoryCapacitySectionProps {
    accountSlug: string
    categoryId: string
    timeSlotsEnabled: boolean
    savedTimeSlotsEnabled: boolean
    timeSlotsControl: React.ReactNode
}

export function CategoryCapacitySection({
    accountSlug,
    categoryId,
    timeSlotsEnabled,
    savedTimeSlotsEnabled,
    timeSlotsControl,
}: CategoryCapacitySectionProps) {
    const { t } = useTranslation()
    const isNew = categoryId === 'new'
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const articlesLoading = useIsLoadingCategoriesOrArticles()
    const capacities = useGetCapacitiesQuery(accountSlug, { skip: isNew })

    const rows = React.useMemo(
        () => (isNew ? [] : categoryCapacityRows(accountSlug, categoryId, articles, capacities.data)),
        [isNew, accountSlug, categoryId, articles, capacities.data],
    )
    const resolve = React.useCallback(
        (groups: TimeSlotGroup[]) => ({ blocks: categoryGroupBlocks(categoryId, groups, categories) }),
        [categoryId, categories],
    )
    const timeSlots = useGroupBlocks(accountSlug, isNew, resolve)

    const turnedOff = !isNew && savedTimeSlotsEnabled && !timeSlotsEnabled
    const losing = React.useMemo(
        () =>
            timeSlots.groups && turnedOff
                ? productsLosingTimeSlots(categoryId, articles, timeSlots.groups, categories)
                : null,
        [timeSlots.groups, turnedOff, categoryId, articles, categories],
    )
    // The warning opens once the count is known (or the groups failed), so it never rewrites itself.
    const countUnknown = timeSlots.failed
    const warningOpen = turnedOff && (countUnknown || (losing ?? 0) > 0)

    const capacityState = categoryCapacityPart(
        { isNew, loading: articlesLoading || capacities.isLoading, failed: capacities.isError, rows },
        t,
    )
    const timeSlotsState = categoryTimeSlotsPart(
        { accountSlug, isNew, enabled: timeSlotsEnabled, failed: timeSlots.failed, resolved: timeSlots.resolved },
        t,
    )

    return (
        <CapacitySectionShell
            capacities={<SectionPart state={capacityState} accountSlug={accountSlug} />}
            timeSlots={
                <>
                    <Reveal open={warningOpen}>
                        <p className="capacity-section-warning" role="status">
                            {countUnknown
                                ? t('capacity.formSection.offWarningUnknown')
                                : t('capacity.formSection.offWarning', { count: losing ?? 0 })}
                            {countUnknown && (
                                <span className="capacity-section-warning-note">
                                    {t('capacity.formSection.offCountUnknown')}
                                </span>
                            )}
                        </p>
                    </Reveal>
                    <SectionPart state={timeSlotsState} accountSlug={accountSlug} details={timeSlots.details} />
                </>
            }
            timeSlotsControl={timeSlotsControl}
        />
    )
}
